import React, { useEffect, useRef, useState } from 'react';
import { Carousel as ReactResponsiveCarousel } from 'react-responsive-carousel';
import { CarouselNavPositionEnum } from '../../../enums/carousel.enums';
import CarouselTransparentNavSection from '../carouselTransparentNavSection';
import CustomCarouselDots from '../customCarouselDots';
import Icon from '../icon';
import GalleryCarouselSlide from './galleryCarouselSlide';
import { documentService, windowService } from '../../../utils/window.utils';
import { getYoutubeVideoId } from '../../../utils/youtube.utils';
import { iconSize } from '../../../constants/icon.constants';
import { downloadImage } from '../../../utils/imageDownload.utils';
import useSitecoreContext from '../../../hooks/useSitecoreContext';

const Carousel = ({ slides }) => {
  const items = slides;
  const interval = 10000; // when you change this change also the $interval variable in GalleryCarousel > style.scss
  const hasMultipleSlides = items ? items.length > 1 : false;
  const [currentSlide, _setCurrentSlide] = useState(0);
  const {sitecoreContext} = useSitecoreContext();
  const slidePos = items ? items[currentSlide] : null;

  const setCurrentSlide = hasMultipleSlides
    ? (index: number): void => {
        if (currentSlide !== index) {
          _setCurrentSlide(index);
        }
      }
    : // eslint-disable-next-line @typescript-eslint/no-empty-function
      (): void => {
        // This is intentional
      };

  const goNext = (): void => (currentSlide === items.length - 1 ? setCurrentSlide(0) : setCurrentSlide(currentSlide + 1));
  const goPrevious = (): void => (currentSlide === 0 ? setCurrentSlide(items.length - 1) : setCurrentSlide(currentSlide - 1));

  //eslint-disable-next-line
  const carouselRef = useRef<any>(null);

  const findLoadedImgSrc = (): string | undefined => {
    const currentSlideDOM: HTMLDListElement = carouselRef?.current?.itemsRef?.[currentSlide];
    const image = currentSlideDOM?.querySelector?.('img');
    // currentSrc = loaded image in picture tag & will be undefined on first load, so src will be fallback
    return image?.currentSrc || image?.src;
  };

  const MediaDownload = (): void => {
    const carouselImageUrl = findLoadedImgSrc();
    const mediaUrl = slidePos?.fields?.useVideo?.value ? slidePos?.fields?.video?.value?.href : carouselImageUrl;
    const url = slidePos ? mediaUrl : null;
    if (!url) return;
    downloadImage(url, sitecoreContext, '');
  };
  
  const isYouTubeUrl = getYoutubeVideoId(slidePos?.fields?.video?.value?.href || '');
  const isMediaUrl = slidePos?.fields?.video?.value?.href && !isYouTubeUrl;
  const isImageUrl = findLoadedImgSrc();
  const showDownloadIcon = slidePos?.fields?.useVideo?.value ? isMediaUrl : isImageUrl;

  useEffect(() => {
    const width = windowService()?.innerWidth / (2 * items?.length);
    documentService()?.querySelector?.('body').style.setProperty('--dots-width', `${width}px`);
  }, [items]);
  return (
    <>
      {items ? (
        <>
          <div className="fullscreen-media__carousel">
            <ReactResponsiveCarousel
              infiniteLoop={true}
              interval={interval}
              autoPlay={true}
              onChange={setCurrentSlide}
              selectedItem={currentSlide}
              showThumbs={false}
              showArrows={false}
              showIndicators={false}
              showStatus={false}
              stopOnHover={false}
              preventMovementUntilSwipeScrollTolerance
              swipeScrollTolerance={100}
              ref={carouselRef}
            >
              {items?.map((itemFields, index) => {
                const slideInfo = { currentSlide, index };
                return <GalleryCarouselSlide slideInfo={slideInfo} fields={itemFields?.fields} RTDescription />;
              })}
            </ReactResponsiveCarousel>
            {showDownloadIcon && (
              <div className="controls__arrow controls__arrow--download" onClick={MediaDownload}>
                <Icon iconName="action-download" size={iconSize.md} />
              </div>
            )}
            {hasMultipleSlides && (
              <div className={'controls'}>
                <div className="controls__arrow controls__arrow--previous" onClick={goPrevious}>
                  <Icon iconName="arrow-chevron-left" size={iconSize.md} />
                </div>
                <CarouselTransparentNavSection position={CarouselNavPositionEnum.LEFT} handleTouchStart={goPrevious} />
                {items.length > 0 && (
                  <ul className={'controls__dots'}>
                    <CustomCarouselDots cardsCount={items.length} activeCard={currentSlide + 1} isDarkMode={true} />
                  </ul>
                )}
                <CarouselTransparentNavSection position={CarouselNavPositionEnum.RIGHT} handleTouchStart={goNext} />
                <div className="controls__arrow controls__arrow--next" onClick={goNext}>
                  <Icon iconName="arrow-chevron-right" size={iconSize.md} />
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        ''
      )}
    </>
  );
};

export default Carousel;
